/**
 * Firebase Analytics - Simple Setup
 * Initializes Firebase Analytics and provides event tracking functions
 */

import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import {
  getAnalytics,
  Analytics,
  logEvent,
  isSupported,
  setUserId,
  setUserProperties,
  setAnalyticsCollectionEnabled,
} from 'firebase/analytics';
import { ENV } from '../config/env';
import { isDevelopment } from '../utils/environment';
import { getStoredEmailCampaignAttribution } from '../utils/emailCampaignAttribution';
import { generateUuid } from '../utils/uuid';

// ============================================================================
// Analytics suppression (e.g., internal users)
// ============================================================================

let analyticsSuppressed = false;
let analyticsSuppressionReason: string | null = null;
const ANALYTICS_SUPPRESSION_STORAGE_KEY = 'analytics_suppressed_v1';
let lastSuppressionLogState: boolean | null = null;

const parseExcludedEmailDomains = (): string[] => {
  const raw = ENV.VITE_ANALYTICS_EXCLUDED_EMAIL_DOMAINS || '';
  return raw
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
};

export const isEmailExcludedFromAnalytics = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  if (!domain) return false;

  const excluded = parseExcludedEmailDomains();
  return excluded.includes(domain);
};

/**
 * Suppress Firebase Analytics tracking for the current session.
 * When suppressed, `trackEvent` and `trackPageView` become no-ops and initialization is skipped.
 */
export const suppressAnalyticsForEmail = (email: string | null | undefined): void => {
  const excluded = isEmailExcludedFromAnalytics(email);
  analyticsSuppressed = excluded;
  analyticsSuppressionReason = excluded ? 'excluded_email_domain' : null;

  if (isDevelopment && lastSuppressionLogState !== excluded) {
    lastSuppressionLogState = excluded;

    console.log('📊 Analytics suppression', {
      suppressed: excluded,
      reason: analyticsSuppressionReason,
      emailDomain: email?.includes('@') ? email.split('@').pop()?.toLowerCase() : null,
      excludedDomains: parseExcludedEmailDomains(),
    });
  }

  // Persist suppression so early init (e.g. app launch) can be skipped on reloads.
  try {
    if (typeof localStorage !== 'undefined') {
      if (excluded) {
        localStorage.setItem(ANALYTICS_SUPPRESSION_STORAGE_KEY, '1');
      } else {
        localStorage.removeItem(ANALYTICS_SUPPRESSION_STORAGE_KEY);
      }
    }
  } catch {
    // Ignore storage errors (private mode / quota / blocked)
  }

  // If analytics is already initialized, disable/enable collection immediately.
  // This prevents further events/pageviews from being sent in the same session.
  try {
    if (analytics && typeof window !== 'undefined') {
      setAnalyticsCollectionEnabled(analytics, !excluded);
      if (excluded) {
        // Clear user id if we've already associated one this session
        setUserId(analytics, null);
      }
    }
  } catch {
    // Never let analytics suppression break the app
  }
};

export const isAnalyticsSuppressed = (): boolean => analyticsSuppressed;

// Initialize suppression from persisted state ASAP (best-effort).
(() => {
  try {
    if (typeof localStorage !== 'undefined') {
      const suppressed = localStorage.getItem(ANALYTICS_SUPPRESSION_STORAGE_KEY) === '1';
      if (suppressed) {
        analyticsSuppressed = true;
        analyticsSuppressionReason = 'persisted_suppression';
      }
    }
  } catch {
    // Ignore storage errors
  }
})();

/**
 * Type definitions for Firebase Analytics global debug tools
 */
export interface FirebaseAnalyticsDebug {
  trackEvent: (eventName: any, eventParams?: any) => void;
  getStatus: () => {
    initialized: boolean;
    initializationAttempted: boolean;
    error: string | null;
  };
  testEvent: () => void;
}

declare global {
  interface Window {
    __firebaseAnalytics?: FirebaseAnalyticsDebug;
    __FIREBASE_ANALYTICS_DEBUG__?: boolean;
  }
}

// ============================================================================
// TypeScript Types for Firebase Analytics Events
// ============================================================================

/**
 * Valid Firebase Analytics event names used in the application
 * Use these constants instead of string literals for better type safety
 */
export const ANALYTICS_EVENTS = {
  // System Events
  ANALYTICS_INITIALIZED: 'analytics_initialized',
  APP_LAUNCHED: 'app_launched',
  TEST_EVENT: 'test_event',

  // Authentication Events
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGN_UP: 'sign_up',
  MFA_VERIFICATION_STARTED: 'mfa_verification_started',
  MFA_VERIFICATION_COMPLETED: 'mfa_verification_completed',
  MFA_VERIFICATION_FAILED: 'mfa_verification_failed',
  GOOGLE_LOGIN_STARTED: 'google_login_started',
  OKTA_LOGIN_STARTED: 'okta_login_started',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  EMAIL_VERIFICATION_SENT: 'email_verification_sent',
  EMAIL_VERIFIED: 'email_verified',
  SESSION_REVOKED: 'session_revoked',
  ALL_SESSIONS_REVOKED: 'all_sessions_revoked',

  // Document & File Management
  DOCUMENT_UPLOAD_STARTED: 'document_upload_started',
  DOCUMENT_UPLOAD_COMPLETED: 'document_upload_completed',
  DOCUMENT_UPLOAD_FAILED: 'document_upload_failed',
  FILE_UPLOAD_FROM_CLOUD: 'file_upload_from_cloud',
  FILE_UPLOAD_FROM_URL: 'file_upload_from_url',
  FILE_UPLOAD_FROM_DEVICE: 'file_upload_from_device',
  DOCUMENT_DELETED: 'document_deleted',
  DOCUMENT_DOWNLOADED: 'document_downloaded',

  // Chat & Messaging
  CHAT_SESSION_STARTED: 'chat_session_started',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_RESPONSE_RECEIVED: 'chat_response_received',
  CHAT_SESSION_DELETED: 'chat_session_deleted',
  CHAT_SESSION_RENAMED: 'chat_session_renamed',
  CHAT_HISTORY_VIEWED: 'chat_history_viewed',
  CHAT_ERROR_OCCURRED: 'chat_error_occurred',

  // Industry & Model Selection
  INDUSTRY_SELECTED: 'industry_selected',
  INDUSTRY_FORM_GENERATED: 'industry_form_generated',
  MODEL_SELECTED: 'model_selected',
  MODEL_UPGRADED: 'model_upgraded',

  // Export Events
  EXPORT_STARTED: 'export_started',
  EXPORT_COMPLETED: 'export_completed',
  EXPORT_FAILED: 'export_failed',
  CLOUD_EXPORT_STARTED: 'cloud_export_started',
  CLOUD_EXPORT_COMPLETED: 'cloud_export_completed',
  CLOUD_EXPORT_FAILED: 'cloud_export_failed',
  CRM_EXPORT_STARTED: 'crm_export_started',
  CRM_EXPORT_COMPLETED: 'crm_export_completed',
  CRM_EXPORT_FAILED: 'crm_export_failed',
  DATABASE_EXPORT_STARTED: 'database_export_started',
  DATABASE_EXPORT_COMPLETED: 'database_export_completed',
  DATABASE_EXPORT_FAILED: 'database_export_failed',

  // Cloud Connection Events
  CLOUD_CONNECTION_INITIATED: 'cloud_connection_initiated',
  CLOUD_CONNECTION_COMPLETED: 'cloud_connection_completed',
  CLOUD_CONNECTION_FAILED: 'cloud_connection_failed',
  CLOUD_CONNECTION_DISCONNECTED: 'cloud_connection_disconnected',
  CLOUD_CONNECTION_RECONNECTED: 'cloud_connection_reconnected',

  // Workspace Events
  WORKSPACE_CREATED: 'workspace_created',
  WORKSPACE_SELECTED: 'workspace_selected',
  WORKSPACE_DELETED: 'workspace_deleted',
  WORKSPACE_RENAMED: 'workspace_renamed',

  // Onboarding Events
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',
  GOAL_SELECTED: 'goal_selected',
  GOAL_COMPLETED: 'goal_completed',
  /** User opened app from Day 1 onboarding email CTA (once per session). */
  ONBOARDING_EMAIL_CTA_LANDING: 'onboarding_email_cta_landing',

  // Subscription & Billing
  PRICING_PAGE_VIEWED: 'pricing_page_viewed',
  PLAN_SELECTED: 'plan_selected',
  BILLING_CYCLE_TOGGLED: 'billing_cycle_toggled',
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
  SUBSCRIPTION_DOWNGRADED: 'subscription_downgraded',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  PAYMENT_METHOD_ADDED: 'payment_method_added',
  PAYMENT_METHOD_UPDATED: 'payment_method_updated',
  INVOICE_DOWNLOADED: 'invoice_downloaded',
  /** Subscription/org gate resolution (post-login vs ProtectedRoute); keep outcomes in sync across surfaces. */
  BILLING_ENTITLEMENT_RESOLVED: 'billing_entitlement_resolved',

  // Profile & Settings
  PROFILE_VIEWED: 'profile_viewed',
  PROFILE_UPDATED: 'profile_updated',
  PASSWORD_CHANGED: 'password_changed',
  MFA_ENABLED: 'mfa_enabled',
  MFA_DISABLED: 'mfa_disabled',
  NOTIFICATION_SETTINGS_UPDATED: 'notification_settings_updated',
  LANGUAGE_CHANGED: 'language_changed',

  // Form & Data Processing
  FORM_DATA_EXTRACTED: 'form_data_extracted',
  FORM_DATA_EDITED: 'form_data_edited',
  FORM_VALIDATION_FAILED: 'form_validation_failed',
  FORM_SUBMITTED: 'form_submitted',
  /** Fired when chat returns structured extracted / summarized document output (form or extraction payload). */
  DOCUMENT_SUMMARIZED: 'document_summarized',

  // Error & System Events
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
  NETWORK_ERROR: 'network_error',

  // Feature Discovery & Support
  FEATURE_DISCOVERED: 'feature_discovered',
  HELP_ACCESSED: 'help_accessed',
  FEEDBACK_SUBMITTED: 'feedback_submitted',

  // Page views (single event for Firebase + linked GA4)
  PAGE_VIEW: 'page_view',
  /** Delegated capture on native buttons and `[role="button"]` (see `subscribeGlobalButtonClickAnalytics`). */
  BUTTON_CLICK: 'button_click',
} as const;

/**
 * Type for valid Firebase Analytics event names
 */
export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/**
 * Event parameters type - flexible to allow any valid Firebase Analytics parameters
 * Includes automatic metadata enrichment (session_id, page_path, etc.)
 */
export type AnalyticsEventParams = Record<string, string | number | boolean>;

/**
 * Helper function to log analytics events with proper type casting
 * Wraps Firebase's logEvent to bypass TypeScript overload resolution issues
 * for custom and built-in event names
 *
 * @param analytics - Firebase Analytics instance
 * @param eventName - Name of the event to track
 * @param params - Optional event parameters
 */
const trackAnalyticsEvent = (
  analytics: Analytics,
  eventName: string,
  params?: AnalyticsEventParams,
): void => {
  // Cast to string to resolve overload resolution issues with custom event names
  logEvent(analytics, eventName as string, params);
};

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: ENV.VITE_FIREBASE_API_KEY,
  authDomain: ENV.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: ENV.VITE_FIREBASE_PROJECT_ID,
  storageBucket: ENV.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: ENV.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: ENV.VITE_FIREBASE_APP_ID,
  measurementId: ENV.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;
let initializationAttempted = false;
let initializationError: Error | null = null;
let initializationPromise: Promise<Analytics | null> | null = null;

/**
 * Log analytics SDK failures for debugging/monitoring.
 * Does not call `logEvent` — a Firebase event would appear in the same user/session
 * stream as real product activity and is easy to misread as something the user did.
 */
const trackAnalyticsError = (
  error: unknown,
  context: string,
  additionalData?: Record<string, string | number | boolean | null | undefined>,
): void => {
  const errorInfo = {
    timestamp: new Date().toISOString(),
    context,
    error: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    analytics_initialized: analytics !== null,
    initialization_attempted: initializationAttempted,
    ...additionalData,
  };

  console.error('🔥 Firebase Analytics Error:', errorInfo);
};

/**
 * Validate event name according to Firebase Analytics rules
 */
const validateEventName = (eventName: string): void => {
  // Check if eventName is provided
  if (!eventName || typeof eventName !== 'string') {
    throw new Error('Event name must be a non-empty string');
  }

  // Check length limit (40 characters)
  if (eventName.length > 40) {
    throw new Error(
      `Event name "${eventName}" exceeds 40 character limit (${eventName.length} characters)`,
    );
  }

  // Check for reserved prefixes
  const reservedPrefixes = ['firebase_', 'google_', 'ga_'];
  const lowerEventName = eventName.toLowerCase();
  for (const prefix of reservedPrefixes) {
    if (lowerEventName.startsWith(prefix)) {
      throw new Error(
        `Event name "${eventName}" uses reserved prefix "${prefix}". Reserved prefixes are not allowed.`,
      );
    }
  }

  // Check for valid characters (alphanumeric and underscores only)
  if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(eventName)) {
    throw new Error(
      `Event name "${eventName}" contains invalid characters. Only letters, numbers, and underscores are allowed, and must start with a letter.`,
    );
  }
};

/**
 * Module-level cache for session ID to avoid repeated localStorage reads
 * Generated once per session and reused for all subsequent events
 */
let cachedSessionId: string | null = null;
let sessionIdInitialized = false;

/**
 * Initialize session ID once from localStorage or generate a new one
 * Called once to populate the module-level cache
 */
const initializeSessionId = (): string | null => {
  if (sessionIdInitialized) {
    return cachedSessionId;
  }

  sessionIdInitialized = true;

  try {
    // Only attempt localStorage access if available
    if (typeof localStorage !== 'undefined') {
      cachedSessionId = localStorage.getItem('analytics_session_id');
      if (!cachedSessionId) {
        cachedSessionId = `session_${Date.now()}_${generateUuid()}`;
        localStorage.setItem('analytics_session_id', cachedSessionId);
      }
    } else {
      // localStorage is unavailable - generate a session ID for this session only (not persisted)
      cachedSessionId = `session_${Date.now()}_${generateUuid()}`;
    }
  } catch (error) {
    // localStorage may throw in private browsing mode or when quota is exceeded
    // Generate a session ID for this session only (not persisted)
    cachedSessionId = `session_${Date.now()}_${generateUuid()}`;
    if (isDevelopment) {
      console.warn('⚠️ localStorage unavailable for session tracking:', error);
    }
  }

  return cachedSessionId;
};

/**
 * Get the cached session ID without repeated localStorage reads
 * Returns null if session ID initialization hasn't been called yet
 */
const getSessionId = (): string | null => {
  if (!sessionIdInitialized) {
    return initializeSessionId();
  }
  return cachedSessionId;
};

/** Firebase GA4 logEvent custom parameter limit (shared by validation and enrichment budgeting). */
const MAX_EVENT_PARAM_COUNT = 25;

/**
 * Auto-added context keys we may drop to stay under MAX_EVENT_PARAM_COUNT before merging
 * session email attribution (email_campaign / email_cta). Order: lowest analytics value first.
 * Worst case: ~15 automatic dimensions from enrich* + caller params; trimming this list reserves
 * headroom so attribution + dense custom props do not exceed the Firebase cap.
 * Trimming never removes keys present on the original caller payload (see callerProvidedKeys).
 */
const REMOVABLE_CONTEXT_PARAM_KEYS: readonly string[] = [
  'page_query',
  'referrer',
  'page_title',
  'viewport_width',
  'viewport_height',
  'environment',
  'app_version',
  'language',
];

/**
 * All auto-enriched keys we may drop to satisfy MAX_EVENT_PARAM_COUNT (never caller keys).
 * Starts with REMOVABLE_CONTEXT_PARAM_KEYS, then lower-priority dimensions / attribution last.
 */
const ENRICHMENT_PARAM_DROP_PRIORITY: readonly string[] = [
  ...REMOVABLE_CONTEXT_PARAM_KEYS,
  'debug_mode',
  'hostname',
  'page_location',
  'platform',
  'page_path',
  'timestamp',
  'session_id',
  'email_cta',
  'email_campaign',
];

const ATTRIBUTION_PARAM_MERGE_PRIORITY = ['email_campaign', 'email_cta'] as const;

type EnrichedEventParams = Record<string, string | number | boolean>;

function attributionMergeSortKey(paramName: string): number {
  const i = (ATTRIBUTION_PARAM_MERGE_PRIORITY as readonly string[]).indexOf(paramName);
  return i === -1 ? ATTRIBUTION_PARAM_MERGE_PRIORITY.length + 1 : i;
}

function sortAttributionEntriesForMerge(entries: [string, string][]): [string, string][] {
  return [...entries].sort(([a], [b]) => {
    const pa = attributionMergeSortKey(a);
    const pb = attributionMergeSortKey(b);
    if (pa !== pb) return pa - pb;
    return a.localeCompare(b);
  });
}

function countEnrichedKeys(enriched: EnrichedEventParams): number {
  return Object.keys(enriched).length;
}

function removeRemovableContextKeys(
  enriched: EnrichedEventParams,
  removeCount: number,
  callerProvidedKeys: ReadonlySet<string>,
): void {
  if (removeCount <= 0) return;
  let removed = 0;
  for (const key of REMOVABLE_CONTEXT_PARAM_KEYS) {
    if (removed >= removeCount) return;
    if (callerProvidedKeys.has(key)) continue;
    if (Object.prototype.hasOwnProperty.call(enriched, key)) {
      delete enriched[key];
      removed += 1;
    }
  }
}

/**
 * Merges onboarding email attribution only when within the Firebase param budget.
 * Drops low-priority auto dimensions first so email_campaign / email_cta win when possible.
 */
function mergeStoredEmailAttributionWithHeadroom(
  enriched: EnrichedEventParams,
  callerProvidedKeys: ReadonlySet<string>,
): void {
  const emailAttr = getStoredEmailCampaignAttribution();
  let pending = Object.entries(emailAttr).filter(([k]) => enriched[k] === undefined);
  if (pending.length === 0) {
    return;
  }

  pending = sortAttributionEntriesForMerge(pending);

  const max = MAX_EVENT_PARAM_COUNT;
  let overBy = countEnrichedKeys(enriched) + pending.length - max;
  removeRemovableContextKeys(enriched, overBy, callerProvidedKeys);

  for (const [k, v] of pending) {
    if (countEnrichedKeys(enriched) >= max) {
      break;
    }
    enriched[k] = v;
  }
}

/** Final guard: strip auto-enriched keys in priority order until ≤ GA4 param cap (never caller keys). */
function trimContextParamsToFitFirebaseLimit(
  enriched: EnrichedEventParams,
  callerProvidedKeys: ReadonlySet<string>,
): void {
  const max = MAX_EVENT_PARAM_COUNT;
  let guard = 0;
  const safety = Object.keys(enriched).length + ENRICHMENT_PARAM_DROP_PRIORITY.length;
  while (countEnrichedKeys(enriched) > max && guard < safety) {
    guard += 1;
    let removed = false;
    for (const key of ENRICHMENT_PARAM_DROP_PRIORITY) {
      if (callerProvidedKeys.has(key)) continue;
      if (Object.prototype.hasOwnProperty.call(enriched, key)) {
        delete enriched[key];
        removed = true;
        break;
      }
    }
    if (!removed) break;
  }
}

const enrichDebugModeForGa4 = (enriched: EnrichedEventParams): void => {
  if (isDevelopment && !enriched.debug_mode) {
    enriched.debug_mode = true;
  }
};

const enrichHostnameParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.hostname) {
    enriched.hostname = window.location.hostname;
  }
};

const enrichPageLocationParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.page_location) {
    enriched.page_location = window.location.href.substring(0, 420);
  }
};

const enrichPageQueryParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.page_query && window.location.search) {
    enriched.page_query = window.location.search.substring(0, 400);
  }
};

const enrichLanguageParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.language) {
    enriched.language = navigator.language || 'unknown';
  }
};

const enrichPlatformParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.platform) {
    enriched.platform = 'web';
  }
};

const enrichEnvironmentParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.environment && ENV.VITE_ENV) {
    enriched.environment = ENV.VITE_ENV;
  }
};

const enrichAppVersionParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.app_version && ENV.VITE_APP_VERSION) {
    enriched.app_version = ENV.VITE_APP_VERSION.substring(0, 100);
  }
};

const enrichPagePathParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.page_path) {
    enriched.page_path = window.location.pathname;
  }
};

const enrichPageTitleParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.page_title) {
    enriched.page_title = document.title?.substring(0, 100) || 'Unknown';
  }
};

const enrichReferrerParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.referrer && document.referrer) {
    enriched.referrer = document.referrer.substring(0, 100);
  }
};

const enrichSessionIdParam = (enriched: EnrichedEventParams): void => {
  if (enriched.session_id) {
    return;
  }
  const sessionId = getSessionId();
  if (sessionId) {
    enriched.session_id = sessionId;
  }
};

const enrichViewportParams = (enriched: EnrichedEventParams): void => {
  if (!enriched.viewport_width || !enriched.viewport_height) {
    enriched.viewport_width = window.innerWidth;
    enriched.viewport_height = window.innerHeight;
  }
};

const enrichTimestampParam = (enriched: EnrichedEventParams): void => {
  if (!enriched.timestamp) {
    enriched.timestamp = new Date().toISOString();
  }
};

/**
 * Browser-only enrichments (window / document / sessionStorage).
 * Split into small helpers to keep per-function cognitive complexity low.
 */
const applyBrowserEventParamEnrichment = (
  enriched: EnrichedEventParams,
  callerProvidedKeys: ReadonlySet<string>,
): void => {
  enrichDebugModeForGa4(enriched);
  enrichHostnameParam(enriched);
  enrichPageLocationParam(enriched);
  enrichPageQueryParam(enriched);
  enrichLanguageParam(enriched);
  enrichPlatformParam(enriched);
  enrichEnvironmentParam(enriched);
  enrichAppVersionParam(enriched);
  enrichPagePathParam(enriched);
  enrichPageTitleParam(enriched);
  enrichReferrerParam(enriched);
  enrichSessionIdParam(enriched);
  enrichViewportParams(enriched);
  enrichTimestampParam(enriched);
  mergeStoredEmailAttributionWithHeadroom(enriched, callerProvidedKeys);
  trimContextParamsToFitFirebaseLimit(enriched, callerProvidedKeys);
};

/**
 * Enrich event parameters with automatic contextual metadata
 */
const enrichEventParams = (
  eventParams?: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> => {
  const callerProvidedKeys = new Set(Object.keys(eventParams || {}));
  const enriched: EnrichedEventParams = { ...(eventParams || {}) };

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    applyBrowserEventParamEnrichment(enriched, callerProvidedKeys);
  }

  return enriched;
};

const MAX_EVENT_PARAM_NAME_LENGTH = 100;
const MAX_EVENT_PARAM_STRING_VALUE_LENGTH = 100;

const RESERVED_EVENT_PARAM_PREFIXES = ['firebase_', 'google_', 'ga_'] as const;

const EVENT_PARAM_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

function assertEventParamCountWithinLimit(
  eventParams: Record<string, string | number | boolean>,
): void {
  const count = Object.keys(eventParams).length;
  if (count > MAX_EVENT_PARAM_COUNT) {
    throw new Error(
      `Too many event parameters (${count}). Maximum allowed is ${MAX_EVENT_PARAM_COUNT} parameters.`,
    );
  }
}

function assertNoReservedEventParamPrefix(key: string): void {
  const lowerKey = key.toLowerCase();
  for (const prefix of RESERVED_EVENT_PARAM_PREFIXES) {
    if (lowerKey.startsWith(prefix)) {
      throw new Error(
        `Parameter name "${key}" uses reserved prefix "${prefix}". Reserved prefixes are not allowed.`,
      );
    }
  }
}

function validateSingleEventParamKey(key: string): void {
  if (!key || typeof key !== 'string') {
    throw new Error(`Parameter key must be a non-empty string, got: ${key}`);
  }

  if (key.length > MAX_EVENT_PARAM_NAME_LENGTH) {
    throw new Error(
      `Parameter name "${key}" exceeds ${MAX_EVENT_PARAM_NAME_LENGTH} character limit (${key.length} characters)`,
    );
  }

  assertNoReservedEventParamPrefix(key);

  if (!EVENT_PARAM_NAME_PATTERN.test(key)) {
    throw new Error(
      `Parameter name "${key}" contains invalid characters. Only letters, numbers, and underscores are allowed, and must start with a letter.`,
    );
  }
}

function validateSingleEventParamValue(key: string, value: string | number | boolean): void {
  if (value === null || value === undefined) {
    throw new Error(
      `Parameter "${key}" has invalid value: null or undefined values are not allowed`,
    );
  }

  const valueType = typeof value;
  if (valueType !== 'string' && valueType !== 'number' && valueType !== 'boolean') {
    throw new Error(
      `Parameter "${key}" has invalid type "${valueType}". Only string, number, and boolean values are allowed.`,
    );
  }

  if (typeof value === 'string' && value.length > MAX_EVENT_PARAM_STRING_VALUE_LENGTH) {
    throw new Error(
      `Parameter "${key}" string value exceeds ${MAX_EVENT_PARAM_STRING_VALUE_LENGTH} character limit (${value.length} characters)`,
    );
  }
}

/**
 * Validate event parameters according to Firebase Analytics rules
 */
const validateEventParams = (eventParams?: Record<string, string | number | boolean>): void => {
  if (!eventParams) {
    return;
  }

  assertEventParamCountWithinLimit(eventParams);

  for (const [key, value] of Object.entries(eventParams)) {
    validateSingleEventParamKey(key);
    validateSingleEventParamValue(key, value);
  }
};

function logSuppressedAnalytics(): void {
  if (isDevelopment) {
    console.log('📊 Firebase Analytics suppressed', { reason: analyticsSuppressionReason });
  }
}

function readCachedAnalyticsOrFailedAttempt(): Analytics | null | undefined {
  if (analyticsSuppressed) {
    logSuppressedAnalytics();
    return null;
  }
  if (analytics) {
    return analytics;
  }
  if (initializationAttempted && !analytics) {
    if (initializationError) {
      console.warn('Firebase Analytics initialization previously failed:', initializationError);
    }
    return null;
  }
  return undefined;
}

function isBrowserEnvironment(): boolean {
  if (typeof window !== 'undefined') {
    return true;
  }
  console.warn('Firebase Analytics: Not in browser environment');
  return false;
}

async function ensureAnalyticsLibrarySupported(): Promise<boolean> {
  const analyticsSupported = await isSupported();
  if (analyticsSupported) {
    return true;
  }
  console.warn(
    'Firebase Analytics: Not supported in this environment (may be blocked by ad blocker)',
  );
  initializationError = new Error('Analytics not supported');
  return false;
}

function findExistingAppMatchingConfig(): FirebaseApp | null {
  const existingApps = getApps();
  return (
    existingApps.find((a) => a.options.appId && a.options.appId === firebaseConfig.appId) ?? null
  );
}

function createFirebaseAppWhenNoneMatch(existingApps: FirebaseApp[]): FirebaseApp {
  const hasDefaultApp = existingApps.length > 0 && existingApps[0].name === '[DEFAULT]';
  if (!hasDefaultApp) {
    return initializeApp(firebaseConfig);
  }
  try {
    return initializeApp(firebaseConfig, 'firebase-analytics');
  } catch {
    console.warn('⚠️ Could not create named Analytics app, trying default app');
    return existingApps[0];
  }
}

function resolveOrCreateFirebaseApp(): void {
  if (app) {
    return;
  }
  const existingApps = getApps();
  const matched = findExistingAppMatchingConfig();
  app = matched ?? createFirebaseAppWhenNoneMatch(existingApps);
}

function logMissingFirebaseAppId(): void {
  if (!app) {
    return;
  }
  console.error('❌ Firebase app is missing appId. Analytics requires appId to work.');
  console.error('Current app config:', {
    projectId: app.options.projectId,
    apiKey: app.options.apiKey ? 'SET' : 'MISSING',
    appId: 'MISSING',
  });
  console.error(
    '💡 Solution: Make sure your Firebase app is initialized with the full config from Firebase Console.',
  );
}

function assertFirebaseAppHasAppId(): boolean {
  if (app?.options.appId) {
    return true;
  }
  logMissingFirebaseAppId();
  initializationError = new Error('Firebase app missing appId - Analytics cannot initialize');
  return false;
}

function enableDevelopmentAnalyticsDebugSurface(): void {
  if (!isDevelopment || typeof window === 'undefined') {
    return;
  }
  window.__FIREBASE_ANALYTICS_DEBUG__ = true;
}

function sendDevelopmentAnalyticsInitializedProbe(instance: Analytics): void {
  if (!isDevelopment) {
    return;
  }
  try {
    trackAnalyticsEvent(instance, ANALYTICS_EVENTS.ANALYTICS_INITIALIZED, {
      timestamp: new Date().toISOString(),
      test: true,
    });
  } catch (testError) {
    console.warn('⚠️ Test event failed:', testError);
  }
}

function logGetAnalyticsFailure(err: unknown): void {
  const errorMsg = err instanceof Error ? err.message : String(err);
  console.error('❌ Failed to initialize Analytics:', errorMsg);
  console.error('💡 Common causes:');
  console.error('   1. appId format is invalid (should be like: 1:123456789:web:abcdef)');
  console.error('   2. Analytics not enabled in Firebase Console');
  console.error('   3. Ad blocker blocking Analytics');
}

function createAnalyticsInstanceOrCaptureError(): Analytics | null {
  if (!app) {
    return null;
  }
  try {
    const instance = getAnalytics(app);
    analytics = instance;
    enableDevelopmentAnalyticsDebugSurface();
    sendDevelopmentAnalyticsInitializedProbe(instance);
    return instance;
  } catch (analyticsInitError) {
    logGetAnalyticsFailure(analyticsInitError);
    initializationError =
      analyticsInitError instanceof Error
        ? analyticsInitError
        : new Error(String(analyticsInitError));
    return null;
  }
}

/**
 * Initialize Firebase Analytics
 * Call this once when your app starts
 */
export const initializeFirebaseAnalytics = async (): Promise<Analytics | null> => {
  const cached = readCachedAnalyticsOrFailedAttempt();
  if (cached !== undefined) {
    return cached;
  }

  initializationAttempted = true;

  try {
    if (!isBrowserEnvironment()) {
      return null;
    }

    if (!(await ensureAnalyticsLibrarySupported())) {
      return null;
    }

    resolveOrCreateFirebaseApp();

    if (!assertFirebaseAppHasAppId()) {
      return null;
    }

    return createAnalyticsInstanceOrCaptureError();
  } catch (error) {
    initializationError = error instanceof Error ? error : new Error(String(error));
    trackAnalyticsError(error, 'initialization', {
      initialization_attempted: initializationAttempted,
      app_configured: !!app,
    });
    return null;
  }
};

async function awaitAnalyticsInitializationIfNeeded(): Promise<void> {
  if (analytics) {
    return;
  }
  if (initializationPromise) {
    analytics = await initializationPromise;
    return;
  }
  initializationPromise = initializeFirebaseAnalytics();
  analytics = await initializationPromise;
  initializationPromise = null;
}

function logDevelopmentFirebaseTrackSuccess(
  eventName: AnalyticsEventName,
  enrichedParams: AnalyticsEventParams,
  eventParams?: AnalyticsEventParams,
): void {
  if (!isDevelopment) {
    return;
  }
  console.log('📊 Event tracked successfully:', eventName, enrichedParams);
  console.log(
    '💡 Check Network tab (filter: "collect" or "google-analytics") to verify the request was sent',
  );
  if (typeof window === 'undefined' || !window.__FIREBASE_ANALYTICS_DEBUG__) {
    return;
  }
  console.log('🔍 Firebase Analytics Debug - Event sent:', {
    eventName,
    eventParams,
    timestamp: new Date().toISOString(),
  });
  console.log(
    '🌐 To verify: Open DevTools → Network tab → Filter by "collect" → Look for POST to google-analytics.com/g/collect',
  );
}

function warnDevelopmentAnalyticsUnavailable(eventName: AnalyticsEventName): void {
  if (!isDevelopment) {
    return;
  }
  console.warn('⚠️ Cannot track event - Analytics not initialized:', eventName);
  console.warn('Analytics status:', getAnalyticsStatus());
}

function logEventToFirebaseWithDevelopmentTrace(
  analyticsInstance: Analytics,
  eventName: AnalyticsEventName,
  eventParams?: AnalyticsEventParams,
): void {
  validateEventName(eventName);
  validateEventParams(eventParams);
  const enrichedParams = enrichEventParams(eventParams);
  assertEventParamCountWithinLimit(enrichedParams);
  try {
    trackAnalyticsEvent(analyticsInstance, eventName, enrichedParams);
    logDevelopmentFirebaseTrackSuccess(eventName, enrichedParams, eventParams);
  } catch (logError) {
    console.error('❌ Error logging event to Firebase:', logError);
    throw logError;
  }
}

/**
 * Track a custom event
 * @param eventName - Name of the event
 * @param eventParams - Optional event parameters
 */
export const trackEvent = async (
  eventName: AnalyticsEventName,
  eventParams?: AnalyticsEventParams,
): Promise<void> => {
  if (analyticsSuppressed) {
    return;
  }
  try {
    await awaitAnalyticsInitializationIfNeeded();
    if (analytics) {
      logEventToFirebaseWithDevelopmentTrace(analytics, eventName, eventParams);
    } else {
      warnDevelopmentAnalyticsUnavailable(eventName);
    }
  } catch (error) {
    trackAnalyticsError(error, 'track_event', {
      event_name: eventName,
      params_count: eventParams ? Object.keys(eventParams).length : 0,
    });
  }
};

// Export analytics instance getter
export const getAnalyticsInstance = async (): Promise<Analytics | null> => {
  if (!analytics) {
    analytics = await initializeFirebaseAnalytics();
  }
  return analytics;
};

/**
 * Check if Firebase Analytics is initialized and working
 */
export const isAnalyticsInitialized = (): boolean => {
  return analytics !== null;
};

/**
 * Get initialization status for debugging
 */
export const getAnalyticsStatus = () => {
  return {
    initialized: analytics !== null,
    initializationAttempted,
    error: initializationError?.message || null,
  };
};

/**
 * Fire-and-forget wrapper for trackEvent (doesn't block execution)
 * Use this when you don't need to wait for the event to be tracked
 *
 * Errors are still tracked through the analytics error system for monitoring
 * and never thrown to the caller, allowing the app to continue normally.
 */
export const trackEventAsync = (
  eventName: AnalyticsEventName,
  eventParams?: AnalyticsEventParams,
): void => {
  if (analyticsSuppressed) {
    return;
  }
  trackEvent(eventName, eventParams).catch((error) => {
    // Use the same error tracking system as trackEvent
    // This ensures errors are logged consistently and can be monitored
    trackAnalyticsError(error, 'track_event_async', {
      event_name: eventName,
      params_count: eventParams ? Object.keys(eventParams).length : 0,
    });
  });
};

const BUTTON_CLICK_DELEGATION_SELECTOR =
  'button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"]';

/**
 * Max length for UI-driven strings (aria, title, value, textContent, etc.) before analytics.
 * Keeps dynamic copy from carrying long names, sentences, or pasted content into GA4.
 */
const MAX_BUTTON_DYNAMIC_LABEL_SLICE = 32;

/** `data-testid` is usually engineer-controlled; allow a bit more room while still bounding size. */
const MAX_BUTTON_TESTID_LABEL_SLICE = 64;

const LIKELY_EMAIL_IN_LABEL_PATTERN = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

function redactLikelyEmailsInButtonLabel(text: string): string {
  return text.replace(LIKELY_EMAIL_IN_LABEL_PATTERN, '[email]');
}

/**
 * Collapses whitespace, redacts email-shaped tokens, truncates to `maxLen`.
 * Returns `null` if there is nothing left to send (empty / whitespace-only).
 */
function preparePotentiallyDynamicButtonLabel(
  raw: string | null | undefined,
  maxLen: number,
): string | null {
  if (raw == null) {
    return null;
  }
  const collapsed = raw.replace(/\s+/g, ' ').trim();
  if (!collapsed) {
    return null;
  }
  const redacted = redactLikelyEmailsInButtonLabel(collapsed);
  const clipped = redacted.length <= maxLen ? redacted : redacted.substring(0, maxLen).trimEnd();
  return clipped.length ? clipped : null;
}

function sanitizeButtonLabel(raw: string): string {
  const t = raw.replace(/\s+/g, ' ').trim();
  if (t.length <= MAX_EVENT_PARAM_STRING_VALUE_LENGTH) {
    return t;
  }
  return t.substring(0, MAX_EVENT_PARAM_STRING_VALUE_LENGTH);
}

/**
 * Best-effort label for analytics (≤ GA4 string param length).
 * Prefer stable `data-testid` values. Other sources are clamped and email-redacted to limit PII.
 */
function resolveButtonClickLabel(el: HTMLElement): string {
  const testIdRaw =
    el.getAttribute('data-testid')?.trim() || el.getAttribute('data-test-id')?.trim();
  const testId = preparePotentiallyDynamicButtonLabel(testIdRaw, MAX_BUTTON_TESTID_LABEL_SLICE);
  if (testId) {
    return sanitizeButtonLabel(`testid:${testId}`);
  }

  const aria = preparePotentiallyDynamicButtonLabel(
    el.getAttribute('aria-label'),
    MAX_BUTTON_DYNAMIC_LABEL_SLICE,
  );
  if (aria) {
    return sanitizeButtonLabel(aria);
  }

  const title = preparePotentiallyDynamicButtonLabel(
    el.getAttribute('title'),
    MAX_BUTTON_DYNAMIC_LABEL_SLICE,
  );
  if (title) {
    return sanitizeButtonLabel(title);
  }

  if (el instanceof HTMLInputElement) {
    const val = preparePotentiallyDynamicButtonLabel(
      el.getAttribute('value'),
      MAX_BUTTON_DYNAMIC_LABEL_SLICE,
    );
    if (val) {
      return sanitizeButtonLabel(val);
    }
    const nm = preparePotentiallyDynamicButtonLabel(el.name, MAX_BUTTON_DYNAMIC_LABEL_SLICE);
    if (nm) {
      return sanitizeButtonLabel(`name:${nm}`);
    }
    const ph = preparePotentiallyDynamicButtonLabel(
      el.getAttribute('placeholder'),
      MAX_BUTTON_DYNAMIC_LABEL_SLICE,
    );
    if (ph) {
      return sanitizeButtonLabel(ph);
    }
  }

  const id = preparePotentiallyDynamicButtonLabel(el.id, MAX_BUTTON_DYNAMIC_LABEL_SLICE);
  if (id) {
    return sanitizeButtonLabel(`id:${id}`);
  }

  const textRaw = el.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  const withinSafeLength = textRaw.length > 0 && textRaw.length <= MAX_BUTTON_DYNAMIC_LABEL_SLICE;
  if (withinSafeLength) {
    const text = preparePotentiallyDynamicButtonLabel(textRaw, MAX_BUTTON_DYNAMIC_LABEL_SLICE);
    if (text) {
      return sanitizeButtonLabel(text);
    }
  }

  return 'unlabeled_button';
}

function isInteractiveControlDisabled(el: HTMLElement): boolean {
  if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
    return el.disabled;
  }
  if (el.hasAttribute('disabled')) {
    return true;
  }
  return el.getAttribute('aria-disabled') === 'true';
}

/**
 * Listen for primary clicks on real `<button>` / submit controls and `[role="button"]`.
 *
 * - Does not fire for `<div onClick>` without `role="button"`.
 * - Add `data-analytics-ignore` on a control or an ancestor to skip tracking.
 * - Respects the same analytics suppression as `trackEventAsync`.
 *
 * @returns Cleanup to remove the listener (e.g. on React unmount).
 */
export const subscribeGlobalButtonClickAnalytics = (): (() => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const onClickCapture = (event: MouseEvent): void => {
    if (event.button !== 0) {
      return;
    }
    if (analyticsSuppressed) {
      return;
    }

    const rawTarget = event.target;
    if (!(rawTarget instanceof Element)) {
      return;
    }

    const el = rawTarget.closest(BUTTON_CLICK_DELEGATION_SELECTOR);
    if (!(el instanceof HTMLElement)) {
      return;
    }

    if (el.closest('[data-analytics-ignore]')) {
      return;
    }

    if (isInteractiveControlDisabled(el)) {
      return;
    }

    const buttonLabel = resolveButtonClickLabel(el);
    const elementTag = el.tagName.toLowerCase();
    const rawType = el.getAttribute('type')?.trim() || '';
    // `<button>` with missing `type` behaves as `submit` per HTML (not `button`).
    const buttonType =
      rawType || (elementTag === 'button' ? 'submit' : elementTag === 'input' ? 'input' : 'none');

    trackEventAsync(ANALYTICS_EVENTS.BUTTON_CLICK, {
      button_label: buttonLabel,
      element_tag: elementTag,
      button_type: buttonType,
    });
  };

  document.addEventListener('click', onClickCapture, true);
  return () => {
    document.removeEventListener('click', onClickCapture, true);
  };
};

/**
 * Track a page view (standard `page_view` for web; visible in Firebase and linked GA4).
 * @param screenName - Name of the screen/page
 * @param screenClass - Class/category of the screen (optional)
 * @param pageTitle - Page title used for GA4 "Page title" dimensions (optional)
 */
export const trackPageView = async (
  screenName: string,
  screenClass?: string,
  pageTitle?: string,
): Promise<void> => {
  if (analyticsSuppressed) {
    return;
  }
  try {
    if (!analytics) {
      analytics = await initializeFirebaseAnalytics();
    }

    if (analytics) {
      const baseParams: AnalyticsEventParams = {
        screen_name: screenName,
        screen_class: screenClass || screenName,
      };
      const params: AnalyticsEventParams =
        pageTitle && pageTitle.trim()
          ? { ...baseParams, page_title: pageTitle.substring(0, 100) }
          : baseParams;

      await trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, params);
    }
  } catch (error) {
    trackAnalyticsError(error, 'track_page_view', {
      screen_name: screenName,
      screen_class: screenClass,
      page_title: pageTitle,
    });
  }
};

/**
 * Set user ID for Firebase Analytics
 * Note: For privacy, consider using a hashed or anonymized identifier
 * @param userId - User identifier (will be hashed/anonymized if needed)
 */
export const setAnalyticsUserId = async (userId: string | null): Promise<void> => {
  try {
    if (analyticsSuppressed) {
      return;
    }
    if (!analytics) {
      analytics = await initializeFirebaseAnalytics();
    }

    if (analytics) {
      if (userId) {
        // For privacy, you might want to hash the userId here
        // For now, using it directly as Firebase Analytics handles privacy
        setUserId(analytics, userId);
        if (isDevelopment) {
          console.log('Firebase Analytics user ID set:', userId);
        }
      } else {
        // Clear user ID on logout
        setUserId(analytics, null);
        if (isDevelopment) {
          console.log('Firebase Analytics user ID cleared');
        }
      }
    }
  } catch (error) {
    trackAnalyticsError(error, 'set_user_id', { user_id_provided: !!userId });
  }
};

function compactUserPropertyEntries(
  properties: Record<string, string | number | boolean | null>,
): Record<string, string | number | boolean> {
  const valid: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value !== null && value !== undefined) {
      valid[key] = value;
    }
  }
  return valid;
}

async function resolveAnalyticsInstanceForUserProperties(): Promise<Analytics | null> {
  if (!analytics) {
    analytics = await initializeFirebaseAnalytics();
  }
  return analytics;
}

/**
 * Set user properties for segmentation and targeting
 * @param properties - Object with user property key-value pairs
 */
export const setAnalyticsUserProperties = async (
  properties: Record<string, string | number | boolean | null>,
): Promise<void> => {
  try {
    if (analyticsSuppressed) {
      return;
    }

    const instance = await resolveAnalyticsInstanceForUserProperties();
    if (!instance) {
      if (isDevelopment) {
        console.warn('⚠️ Cannot set user properties - Analytics not initialized');
      }
      return;
    }

    const validProperties = compactUserPropertyEntries(properties);
    if (Object.keys(validProperties).length === 0) {
      return;
    }

    setUserProperties(instance, validProperties);
    if (isDevelopment) {
      console.log('🏷️ User properties set:', validProperties);
    }
  } catch (error) {
    trackAnalyticsError(error, 'set_user_properties', {
      properties_count: Object.keys(properties).length,
    });
  }
};
